import React, { useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react'
import { designTokens } from '@/utils/design-tokens'
import { CalendarProps } from './types'
import { getCalendarDays, getCalendarWeeks, isSameDay, isDateInRange } from './utils'
import MonthYearSelector from './MonthYearSelector'
import WeekPicker from './WeekPicker'
import MonthPicker from './MonthPicker'
import { TaskItemService } from '@/api/generated/services/TaskItemService'

const Calendar: React.FC<CalendarProps> = ({
  selectedStartDate,
  selectedEndDate,
  onDateSelect,
  onRangeComplete,
  minDate,
  maxDate,
  onClose,
  setWeekSelectionMode: setParentWeekSelectionMode,
  workspaceId,
  selectionMode = 'free'
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date()
    return new Date(today.getFullYear(), today.getMonth(), 1)
  })

  // State for week range selection
  const [weekSelectionMode, setWeekSelectionMode] = useState(false)
  const [selectedStartWeek, setSelectedStartWeek] = useState<number | null>(null)
  const [selectedEndWeek, setSelectedEndWeek] = useState<number | null>(null)

  // State for month/year selector
  const [showMonthYearSelector, setShowMonthYearSelector] = useState(false)

  // State for "전체" button loading
  const [isAllLoading, setIsAllLoading] = useState(false)

  const monthNames = [
    '01월', '02월', '03월', '04월', '05월', '06월',
    '07월', '08월', '09월', '10월', '11월', '12월'
  ]

  const weekDays = ['월', '화', '수', '목', '금', '토', '일']

  const calendarDays = useMemo(() => {
    return getCalendarDays(currentMonth.getFullYear(), currentMonth.getMonth())
  }, [currentMonth])

  const calendarWeeks = useMemo(() => {
    return getCalendarWeeks(currentMonth.getFullYear(), currentMonth.getMonth())
  }, [currentMonth])

  const handlePrevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))
  }

  const handleNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))
  }

  const handleMonthTextClick = () => {
    setShowMonthYearSelector(!showMonthYearSelector)
  }

  const handleMonthYearRangeSelect = (startDate: Date, endDate: Date) => {
    onDateSelect(startDate, 'start')
    onDateSelect(endDate, 'end')
    onRangeComplete(startDate, endDate)
    setShowMonthYearSelector(false)
    setTimeout(() => onClose(), 200)
  }

  const handleAllClick = async () => {
    if (!workspaceId || isAllLoading) return
    setIsAllLoading(true)
    try {
      const response = await TaskItemService.appsTaskViewsGetTaskItemDateRange({
        workspaceId
      })
      if (response.start_at && response.end_at) {
        const startDate = new Date(response.start_at)
        const endDate = new Date(response.end_at)
        onDateSelect(startDate, 'start')
        onDateSelect(endDate, 'end')
        onRangeComplete(startDate, endDate)
        setTimeout(() => onClose(), 200)
      }
    } catch (err) {
      console.error('Failed to fetch task item date range:', err)
    } finally {
      setIsAllLoading(false)
    }
  }

  // 주/월 전용 모드에서 피커 선택 완료 처리 (선택 반영 후 닫기)
  const handleUnitPickerSelect = (start: Date, end: Date) => {
    onDateSelect(start, 'start')
    onDateSelect(end, 'end')
    onRangeComplete(start, end)
    setTimeout(() => onClose(), 200)
  }

  const handleDateClick = (date: Date) => {
    if (minDate && date < minDate) return
    if (maxDate && date > maxDate) return

    // If in week selection mode, reset it
    if (weekSelectionMode) {
      setWeekSelectionMode(false)
      setSelectedStartWeek(null)
      setSelectedEndWeek(null)
      setParentWeekSelectionMode(false)
    }

    // For period mode, allow manual date range selection
    if (!selectedStartDate || (selectedStartDate && selectedEndDate)) {
      // Start new selection - clear previous selection and set start date
      onDateSelect(date, 'start')
      onDateSelect(null, 'end') // Clear end date when starting new selection
    } else if (selectedStartDate && !selectedEndDate) {
      // Complete the range
      if (date < selectedStartDate) {
        // If end date is before start date, swap them
        onDateSelect(date, 'start')
        onDateSelect(selectedStartDate, 'end')
        onRangeComplete(date, selectedStartDate)
      } else {
        onDateSelect(date, 'end')
        onRangeComplete(selectedStartDate, date)
      }
      setTimeout(() => onClose(), 200)
    }
  }

  const handleWeekClick = (weekIndex: number) => {
    const weekStartIndex = weekIndex * 7
    const weekStartDate = calendarDays[weekStartIndex]
    const weekEndDate = calendarDays[weekStartIndex + 6]

    if (!weekSelectionMode) {
      // First click - enter week selection mode and select start week
      setWeekSelectionMode(true)
      setSelectedStartWeek(weekIndex)
      setSelectedEndWeek(null)
      // Notify parent that we're entering week selection mode
      setParentWeekSelectionMode(true)
      onDateSelect(weekStartDate, 'start')
      onDateSelect(weekEndDate, 'end') // Show the first selected week
    } else {
      // Second click - complete the week range selection
      if (selectedStartWeek !== null) {
        const startWeekIndex = Math.min(selectedStartWeek, weekIndex)
        const endWeekIndex = Math.max(selectedStartWeek, weekIndex)

        // Calculate range from start of first week to end of last week
        const rangeStartIndex = startWeekIndex * 7
        const rangeEndIndex = endWeekIndex * 7 + 6
        const rangeStartDate = calendarDays[rangeStartIndex]
        const rangeEndDate = calendarDays[rangeEndIndex]

        setSelectedEndWeek(weekIndex)
        onDateSelect(rangeStartDate, 'start')
        onDateSelect(rangeEndDate, 'end')
        onRangeComplete(rangeStartDate, rangeEndDate)

        // Reset week selection mode
        setTimeout(() => {
          setWeekSelectionMode(false)
          setSelectedStartWeek(null)
          setSelectedEndWeek(null)
          // Notify parent that week selection mode is done
          setParentWeekSelectionMode(false)
          onClose()
        }, 200)
      }
    }
  }

  const getDayStyle = (date: Date): React.CSSProperties => {
    const isCurrentMonth = date.getMonth() === currentMonth.getMonth()
    const isSelected = selectedStartDate && isSameDay(date, selectedStartDate) ||
                     selectedEndDate && isSameDay(date, selectedEndDate)
    const isInRange = selectedStartDate && selectedEndDate &&
                     isDateInRange(date, selectedStartDate, selectedEndDate)
    const isToday = isSameDay(date, new Date())
    const isDisabled = (minDate && date < minDate) || (maxDate && date > maxDate)

    let backgroundColor = 'transparent'
    let color = designTokens.colors.gray[6]

    if (!isCurrentMonth) {
      color = designTokens.colors.gray[3]
    }

    if (isSelected) {
      backgroundColor = designTokens.colors.primary.DEFAULT
      color = designTokens.colors.white
    } else if (isInRange) {
      backgroundColor = `${designTokens.colors.primary.DEFAULT}20`
      color = designTokens.colors.primary.dark
    }

    if (isToday && !isSelected) {
      color = designTokens.colors.primary.DEFAULT
    }

    if (isDisabled) {
      color = designTokens.colors.gray[2]
      backgroundColor = 'transparent'
    }

    return {
      backgroundColor,
      color,
      opacity: isDisabled ? 0.5 : 1,
      cursor: isDisabled ? 'not-allowed' : 'pointer',
      border: isToday && !isSelected ? `1px solid ${designTokens.colors.primary.DEFAULT}` : '1px solid transparent',
      borderRadius: '4px',
      transition: 'all 0.15s ease'
    }
  }

  const getDayHoverStyle = (): React.CSSProperties => ({
    backgroundColor: designTokens.colors.gray[0]
  })

  const getWeekStyle = (weekIndex: number): React.CSSProperties => {
    let backgroundColor = designTokens.colors.gray[0]
    let color = designTokens.colors.gray[4]

    if (weekSelectionMode && selectedStartWeek !== null) {
      // In selection mode - highlight selected range
      if (selectedEndWeek !== null) {
        const startIdx = Math.min(selectedStartWeek, selectedEndWeek)
        const endIdx = Math.max(selectedStartWeek, selectedEndWeek)
        if (weekIndex >= startIdx && weekIndex <= endIdx) {
          backgroundColor = designTokens.colors.primary.DEFAULT
          color = designTokens.colors.white
        }
      } else if (weekIndex === selectedStartWeek) {
        // Only start week selected
        backgroundColor = designTokens.colors.primary.DEFAULT
        color = designTokens.colors.white
      }
    }

    return {
      fontFamily: designTokens.typography.fontFamily.sans[0],
      fontSize: designTokens.typography.fontSize['body-small'][0],
      fontWeight: 600,
      color,
      backgroundColor,
      borderRadius: '4px',
      transition: 'background-color 0.15s ease',
      cursor: 'pointer'
    }
  }

  // 주단위 모드: 일 달력 대신 주 목록 피커 (한 번의 클릭으로 주 선택)
  if (selectionMode === 'week') {
    return (
      <WeekPicker
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
        onSelect={handleUnitPickerSelect}
      />
    )
  }

  // 월단위 모드: 일 달력 대신 12개월 그리드 피커 (한 번의 클릭으로 월 선택)
  if (selectionMode === 'month') {
    return (
      <MonthPicker
        selectedStartDate={selectedStartDate}
        onSelect={handleUnitPickerSelect}
      />
    )
  }

  return (
    <div style={{ padding: '16px', width: '420px', maxWidth: '90vw', minWidth: '320px' }}>
      {/* Calendar Header with Month Navigation */}
      <div
        className="flex items-center justify-between mb-4"
        style={{ paddingBottom: '12px', borderBottom: `1px solid ${designTokens.colors.gray[1]}` }}
      >
        {/* Month Text with Dropdown Icon + All Range Button */}
        <div className="flex items-center gap-2">
          <div style={{ position: 'relative' }}>
            <h3
              className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
              style={{
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-medium-bold'][0],
                fontWeight: 600,
                color: designTokens.colors.gray[6]
              }}
              onClick={handleMonthTextClick}
            >
              <span>월단위 선택</span>
              <ChevronDown className="w-4 h-4" />
            </h3>

            <MonthYearSelector
              isOpen={showMonthYearSelector}
              onClose={() => setShowMonthYearSelector(false)}
              onRangeSelect={handleMonthYearRangeSelect}
              currentYear={currentMonth.getFullYear()}
              currentMonth={currentMonth.getMonth()}
            />
          </div>
          {workspaceId && (
            <button
              onClick={handleAllClick}
              disabled={isAllLoading}
              className="flex items-center justify-center rounded transition-colors"
              style={{
                padding: '4px 12px',
                fontFamily: designTokens.typography.fontFamily.sans[0],
                fontSize: designTokens.typography.fontSize['body-small'][0],
                fontWeight: 600,
                color: isAllLoading ? designTokens.colors.gray[3] : designTokens.colors.gray[6],
                backgroundColor: designTokens.colors.gray[0],
                border: `1px solid ${designTokens.colors.gray[2]}`,
                cursor: isAllLoading ? 'not-allowed' : 'pointer',
                borderRadius: '4px',
              }}
              onMouseEnter={(e) => {
                if (!isAllLoading) {
                  e.currentTarget.style.backgroundColor = designTokens.colors.gray[1]
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = designTokens.colors.gray[0]
              }}
            >
              {isAllLoading ? '...' : '전체범위'}
            </button>
          )}
        </div>

        {/* Navigation Arrows */}
        <div className="flex items-center gap-1">
          <button
            onClick={handlePrevMonth}
            className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-50 transition-colors"
            style={{ color: designTokens.colors.gray[5] }}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span>{currentMonth.getFullYear()}년 {monthNames[currentMonth.getMonth()]}</span>
          <button
            onClick={handleNextMonth}
            className="flex items-center justify-center w-8 h-8 rounded hover:bg-gray-50 transition-colors"
            style={{ color: designTokens.colors.gray[5] }}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Week Days Header */}
      <div className="grid grid-cols-8 gap-1 mb-2">
        {/* Week number header */}
        <div
          className="flex items-center justify-center h-8"
          style={{
            fontFamily: designTokens.typography.fontFamily.sans[0],
            fontSize: designTokens.typography.fontSize['body-small'][0],
            fontWeight: 600,
            color: designTokens.colors.gray[4]
          }}
        >
          주
        </div>
        {weekDays.map(day => (
          <div
            key={day}
            className="flex items-center justify-center h-8"
            style={{
              fontFamily: designTokens.typography.fontFamily.sans[0],
              fontSize: designTokens.typography.fontSize['body-small'][0],
              fontWeight: 600,
              color: designTokens.colors.gray[4]
            }}
          >
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days with Week Numbers */}
      <div className="grid grid-cols-8 gap-1">
        {calendarWeeks.map((weekNum, weekIndex) => (
          <React.Fragment key={`week-${weekIndex}`}>
            {/* Week number cell */}
            <div
              className="flex items-center justify-center h-8"
              style={getWeekStyle(weekIndex)}
              onClick={() => handleWeekClick(weekIndex)}
              onMouseEnter={(e) => {
                if (!weekSelectionMode || (weekSelectionMode && selectedStartWeek !== weekIndex)) {
                  e.currentTarget.style.backgroundColor = designTokens.colors.gray[2]
                }
              }}
              onMouseLeave={(e) => {
                Object.assign(e.currentTarget.style, getWeekStyle(weekIndex))
              }}
            >
              {weekNum}
            </div>

            {/* Days for this week */}
            {calendarDays.slice(weekIndex * 7, (weekIndex + 1) * 7).map((date, dayIndex) => (
              <button
                key={`${weekIndex}-${dayIndex}`}
                onClick={() => handleDateClick(date)}
                className="flex items-center justify-center h-8 text-sm transition-all duration-150"
                style={{
                  ...getDayStyle(date),
                  fontFamily: designTokens.typography.fontFamily.sans[0],
                  fontSize: designTokens.typography.fontSize['body-small'][0]
                }}
                onMouseEnter={(e) => {
                  if (!getDayStyle(date).backgroundColor || getDayStyle(date).backgroundColor === 'transparent') {
                    Object.assign(e.currentTarget.style, getDayHoverStyle())
                  }
                }}
                onMouseLeave={(e) => {
                  Object.assign(e.currentTarget.style, getDayStyle(date))
                }}
                disabled={(minDate && date < minDate) || (maxDate && date > maxDate)}
              >
                {date.getDate()}
              </button>
            ))}
          </React.Fragment>
        ))}
      </div>

      {/* Period Info */}
      <div
        className="mt-4 pt-3 text-center"
        style={{
          borderTop: `1px solid ${designTokens.colors.gray[1]}`,
          fontFamily: designTokens.typography.fontFamily.sans[0],
          fontSize: designTokens.typography.fontSize['body-xsmall'][0],
          color: weekSelectionMode ? designTokens.colors.primary.DEFAULT : designTokens.colors.gray[4],
          fontWeight: weekSelectionMode ? 600 : 400
        }}
      >
        {weekSelectionMode
          ? '종료 주차를 선택해주세요 (여러 주 선택 가능)'
          : '주차 클릭: 주간 범위 선택 (2번 클릭) | 날짜 클릭: 기간 선택'}
      </div>
    </div>
  )
}

export default Calendar